<template>
  <section v-if="cityTags.length" v-what-else-view class="sights-wrap">
    <div>
      <Wrap>
        <AttractionsTitle :title="sightsTitle" :to="sightsUrl" @show-more-click="onViewAllClick" />
      </Wrap>

      <div class="sights">
        <UiSlider
          :slides="cityTags"
          mousewheel
          arrows-always-visible
          :slides-per-view="slidesPerView"
          :space-between="16"
          :options="{
            slidesPerGroup: slidePerGroup,
            slidesOffsetAfter: sliderOffset,
            slidesOffsetBefore: sliderOffset,
            preventClicks: false,
            preventClicksPropagation: false,
            focusableElements: 'input, select, option, textarea, button, video, label, a',
          }"
        >
          <template #slide="{ slide }">
            <Attraction :attraction="slide" />
          </template>
          <template #rightArrowIcon>
            <IconBase name="right-arrow" />
          </template>
          <template #leftArrowIcon>
            <IconBase name="right-arrow" class="mirror" />
          </template>
        </UiSlider>
      </div>

      <Wrap>
        <div class="btn-wrap-more">
          <UiButton
            v-if="showMoreButton && sightsUrl"
            :href="sightsUrl"
            :is-full-width="isMobile"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            :text="t('nearby.showAll')"
            data-test="listing-sights-show-all"
            @click="onViewAllClick"
          />
        </div>
      </Wrap>
    </div>
    <div id="sights" class="fake-anchor" />
  </section>
</template>

<script lang="ts" setup>
import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
import { useI18n } from '@experience/i18n';
import { UiSlider } from '@experience/vue3-kit';
import { computed, onMounted, onUnmounted, ref } from 'vue';

import { useAnalytics } from '@/shared/lib/analytics';
import { IconBase } from '@/shared/ui/iconBase';
import Wrap from '@/shared/ui/Wrap.vue';
import { useScreenSize } from '@/shared/utils/breakpoints';

import Attraction from './Attraction.vue';
import AttractionsTitle from './AttractionsTitle.vue';
import ru from './i18n/ru.json';

import type { NearbyAttraction } from './models';

const props = withDefaults(
  defineProps<{
    attractions?: NearbyAttraction[];
    title?: string;
    sightsUrl?: string;
  }>(),
  {
    attractions: () => [],
    title: '',
    sightsUrl: '',
  },
);

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { isMobile, isTablet } = useScreenSize();
const { trackEventImmediately, useViewTracker } = useAnalytics();
const vWhatElseView = useViewTracker('sight:what-else.view');

const cityTags = computed(() => props.attractions);
const sightsTitle = computed(() => props.title || t('nearby.title'));
const sightsUrl = computed(() => props.sightsUrl);

const sliderOffset = computed(() => {
  if (isTablet.value && !isMobile.value) {
    return 20;
  }

  if (isMobile.value) {
    return 24;
  }

  return 0;
});

type SlidesPerViewConfig = {
  min?: number;
  max?: number;
  slides: number | ((total: number) => number);
};

const slidesPerViewConfig: SlidesPerViewConfig[] = [
  { max: 359, slides: (total) => (total === 1 ? 1 : 1.45) },
  { min: 360, max: 439, slides: (total) => (total === 1 ? 1.05 : 1.62) },
  { min: 440, max: 679, slides: (total) => (total < 3 ? 2 : 2.01) },
  {
    min: 680,
    max: 767,
    slides: (total) => {
      if (total <= 2) return 2;
      if (total === 3) return 3;
      return 3.2;
    },
  },
  {
    min: 768,
    max: 799,
    slides: (total) => {
      if (total <= 2) return 2;
      if (total === 3) return 3;
      return 3.6;
    },
  },
  { min: 800, max: 859, slides: (total) => (total < 5 ? 4 : 4.15) },
  { min: 860, max: 919, slides: (total) => (total < 5 ? 4 : 4.3) },
  {
    min: 920,
    max: 1023,
    slides: (total) => {
      if (total < 5) return 4;
      if (total === 5) return 5;
      return 5.2;
    },
  },
  { min: 1024, max: 1199, slides: (total) => (total < 5 ? 4 : 5) },
  {
    min: 1200,
    slides: (total) => {
      if (total < 5) return 4;
      if (total === 5) return 5;
      return 6;
    },
  },
];

const getSlidesPerView = (total: number): number => {
  const config = slidesPerViewConfig.find(({ min, max }) => {
    if (min && max) return window.matchMedia(`(min-width: ${min}px) and (max-width: ${max}px)`).matches;
    if (min) return window.matchMedia(`(min-width: ${min}px)`).matches;
    if (max) return window.matchMedia(`(max-width: ${max}px)`).matches;
    return false;
  });

  if (!config) return 4;
  return typeof config.slides === 'function' ? config.slides(total) : config.slides;
};

const slidesPerView = ref(0);
const slidesTotalCount = computed(() => cityTags.value.length);
const slidePerGroup = computed(() => (isMobile.value || isTablet.value ? 1 : slidesPerView.value));
const showMoreButton = computed(() => slidesTotalCount.value > slidesPerView.value);

const updateSlidesPerView = () => {
  slidesPerView.value = getSlidesPerView(slidesTotalCount.value);
};

const onViewAllClick = async () => {
  await trackEventImmediately('sight:what-else:view-all.click');
};

onMounted(() => {
  updateSlidesPerView();
  window.addEventListener('resize', updateSlidesPerView);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateSlidesPerView);
});
</script>

<style scoped lang="scss">
.sights-wrap {
  position: relative;
  padding: 80px 0 0;
  overflow: hidden;

  .btn-wrap-more {
    display: none;
    margin-top: 24px;

    :deep(.button) {
      width: 100%;
    }

    @include mobile {
      margin-top: 16px;
    }

    @include tablet {
      display: block;
    }
  }

  @include tablet {
    padding: 56px 0 0;
  }

  @include mobile {
    padding: 48px 0 0;
  }
}

.mirror {
  transform: scaleX(-1);
}

.sights {
  width: 100vw;
  min-width: var(--wrap-min-width);
  max-width: var(--wrap-max-width);
  padding: 0 var(--wrap-offset);
  margin: 0 auto;

  @include tablet {
    box-sizing: border-box;
    padding: 0;
  }
}

:deep(.btn-slider.btn-prev),
:deep(.btn-slider.btn-next) {
  top: 50% !important;
}

:deep(.btn-slider) {
  @include tablet {
    display: none;
  }
}
</style>
